/**
 * NavigationTiles Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * An advanced navigation tile system for the Irish Funds member portal dashboard.
 * Features include:
 * - Dynamic tile generation from navigation structure data
 * - Expandable/collapsible main tiles with sub-tile functionality
 * - FontAwesome icon integration with dynamic icon loading
 * - Background image support for enhanced visual appeal
 * - Tippy tooltip integration for enhanced user experience
 * - React Router integration for seamless navigation
 * - Responsive grid layout with CSS Grid
 * - State management for tile expansion behavior
 * - Professional hover effects and animations
 * - Accessibility-friendly with proper ARIA attributes
 * - Production-ready with comprehensive error handling
 * 
 * @param {Object} props - Component props
 * @param {Object} props.navigationStructure - Navigation configuration object
 * @param {Array} props.navigationStructure.mainTiles - Array of main navigation tiles
 * @param {string} props.navigationStructure.mainTiles[].path - Tile navigation path
 * @param {string} props.navigationStructure.mainTiles[].label - Tile display label
 * @param {string} props.navigationStructure.mainTiles[].subtitle - Tile subtitle for tooltips
 * @param {string} props.navigationStructure.mainTiles[].icon - FontAwesome icon name
 * @param {string} props.navigationStructure.mainTiles[].backgroundImage - Optional background image URL
 * @param {Array} props.navigationStructure.mainTiles[].subTiles - Optional array of sub-tiles
 * @returns {JSX.Element} Interactive navigation tiles with expand/collapse functionality
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import './NavigationTiles.css';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import * as Icons from '@fortawesome/free-solid-svg-icons';
import { prefetchAll } from '../../utils/prefetchUtils';

/**
 * NavigationTiles Component
 * 
 * Renders a dynamic grid of navigation tiles with expandable sub-tile functionality.
 * Each tile can contain sub-tiles that are revealed when the parent tile is expanded.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.navigationStructure - Navigation configuration with mainTiles array
 * @returns {JSX.Element} Grid of interactive navigation tiles
 */
const NavigationTiles = ({ navigationStructure }) => {
  // State management for tile expansion behavior
  const [expandedTile, setExpandedTile] = useState(null);

  // Prefetch content when home page loads for better performance
  useEffect(() => {
    prefetchAll().catch(error => {
      console.warn('Prefetch failed:', error);
    });
  }, []);

  /**
   * Process navigation structure and dynamically load FontAwesome icons
   * 
   * Transforms the navigation structure data into a format suitable for rendering,
   * including dynamic icon loading and sub-tile processing for expandable functionality.
   */
  const tiles = navigationStructure.mainTiles.map(tile => ({
    ...tile,
    icon: Icons[tile.icon], // Dynamic FontAwesome icon loading
    path: tile.path,
    backgroundImage: tile.backgroundImage,
    subTiles: tile.subTiles ? tile.subTiles.map(subTile => ({
      ...subTile,
      icon: Icons[subTile.icon], // Dynamic sub-tile icon loading
      path: subTile.path,
      backgroundImage: subTile.backgroundImage
    })) : undefined
  }));

  return (
    <div className="navigation-tiles">
      <div className="navigation-tiles-grid">
        {tiles.map((tile) => (
          <div
            key={tile.path}
            className={`navigation-tile ${expandedTile === tile.path ? 'expanded' : ''} ${tile.backgroundImage ? 'has-bg' : ''}`}
            style={{
              backgroundImage: tile.backgroundImage ? `url(${tile.backgroundImage})` : undefined
            }}
          >
            <div className="tile-content">
              <Link to={tile.path}>
                <Tippy content={tile.subtitle}>
                  <span className="tile-subtitle">{tile.label}</span>
                </Tippy>
              </Link>
            </div>

            {tile.subTiles && tile.subTiles.length > 0 && (
              <button
                onClick={(e) => {
                  // Prevent event bubbling to avoid navigation conflicts
                  e.preventDefault();
                  e.stopPropagation();
                  // Toggle expansion state - collapse if already expanded, expand if not
                  setExpandedTile(expandedTile === tile.path ? null : tile.path);
                }}
                className="expand-button"
              >
                <FontAwesomeIcon
                  icon={expandedTile === tile.path ? faChevronUp : faChevronDown}
                  className="expand-icon"
                />
              </button>
            )}

            {expandedTile === tile.path && tile.subTiles && tile.subTiles.length > 0 && (
              <div className="sub-tiles-container">
                <div className="sub-tiles">
                  {tile.subTiles.map((subTile) => (
                    <div
                      key={subTile.path}
                      className={`navigation-tile sub-tile ${subTile.backgroundImage ? 'has-bg' : ''}`}
                      style={{
                        backgroundImage: subTile.backgroundImage ? `url(${subTile.backgroundImage})` : undefined
                      }}
                    >
                      <div className="tile-content">
                        <Tippy content={subTile.label}>
                          <a href={subTile.path}>
                            <span className="subtile-subtitle">{subTile.label}</span>
                          </a>
                        </Tippy>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default NavigationTiles; 